import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';

import { auth, seed } from '@/lib/api';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Commissions from '@/pages/Commissions';
import Agents from '@/pages/Agents';
import Statements from '@/pages/Statements';
import Expenses from '@/pages/Expenses';
import Settings from '@/pages/Settings';
import Import from '@/pages/Import';
import PortalDashboard from '@/pages/PortalDashboard';
import ReferralLeads from '@/pages/ReferralLeads';
import ReferrerLogin from '@/pages/ReferrerLogin';
import ReferrerDashboard from '@/pages/ReferrerDashboard';
import Reports from '@/pages/Reports';
import AgentSignup from '@/pages/AgentSignup';
import AuditLogs from '@/pages/AuditLogs';
import NotificationCenter from '@/pages/NotificationCenter';
import EmailTemplates from '@/pages/EmailTemplates';
import Accounting from '@/pages/Accounting';
import MyTaxSlips from '@/pages/MyTaxSlips';
import Vendors from '@/pages/Vendors';
import ClientPortalLogin from '@/pages/ClientPortalLogin';
import ClientPortalDashboard from '@/pages/ClientPortalDashboard';
import Sidebar from '@/components/Sidebar';

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Business Unit Context (Mortgage vs Insurance & Investment)
const BusinessUnitContext = createContext(null);

export const useBusinessUnit = () => useContext(BusinessUnitContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function BusinessUnitProvider({ children }) {
  const [businessUnit, setBusinessUnit] = useState(() => {
    return localStorage.getItem('businessUnit') || 'mortgage';
  });

  const switchBusinessUnit = (unit) => {
    localStorage.setItem('businessUnit', unit);
    setBusinessUnit(unit);
  };

  return (
    <BusinessUnitContext.Provider value={{ businessUnit, switchBusinessUnit }}>
      {children}
    </BusinessUnitContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <main className={`flex-1 transition-all duration-300 overflow-x-hidden ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-6 md:p-8 max-w-full">
          {children}
        </div>
      </main>
    </div>
  );
}

function AppContent() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Seed data on first load
    const seedData = async () => {
      try {
        await seed();
      } catch (err) {
        // Data already seeded or error - ignore
      }
    };
    if (user) {
      seedData();
    }
  }, [user]);

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      {/* Referrer Portal Routes (Public) */}
      <Route path="/referrer/login" element={<ReferrerLogin />} />
      <Route path="/referrer/dashboard" element={<ReferrerDashboard />} />
      {/* Agent Signup Routes (Public) */}
      <Route path="/agent/signup" element={<AgentSignup />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout><Dashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commissions" element={
        <ProtectedRoute>
          <AppLayout><Commissions /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/agents" element={
        <ProtectedRoute>
          <AppLayout><Agents /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/statements" element={
        <ProtectedRoute>
          <AppLayout><Statements /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/expenses" element={
        <ProtectedRoute>
          <AppLayout><Expenses /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <AppLayout><Settings /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/import" element={
        <ProtectedRoute>
          <AppLayout><Import /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <AppLayout><Reports /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute>
          <AppLayout><AuditLogs /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <AppLayout><NotificationCenter /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/email-templates" element={
        <ProtectedRoute>
          <AppLayout><EmailTemplates /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/accounting" element={
        <ProtectedRoute>
          <AppLayout><Accounting /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute>
          <AppLayout><Vendors /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/my-tax-slips" element={
        <ProtectedRoute>
          <AppLayout><MyTaxSlips /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/portal" element={
        <ProtectedRoute>
          <AppLayout><PortalDashboard /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/portal/referral-leads" element={
        <ProtectedRoute>
          <AppLayout><ReferralLeads division={null} /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/referral-leads" element={
        <ProtectedRoute>
          <AppLayout><ReferralLeadsWrapper /></AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Client Portal Routes (No Auth Required - Uses Own Auth System) */}
      <Route path="/client-portal" element={<ClientPortalLogin />} />
      <Route path="/client-portal/dashboard" element={<ClientPortalDashboard />} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Wrapper to pass current business unit as division
function ReferralLeadsWrapper() {
  const { businessUnit } = useBusinessUnit();
  return <ReferralLeads division={businessUnit} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessUnitProvider>
          <AppContent />
          <Toaster position="top-right" richColors />
        </BusinessUnitProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
